import { useState, useEffect } from "react";
import { useI18n } from "@/lib/i18n";
import { Link } from "react-router-dom";
import { Cookie } from "lucide-react";

const COOKIE_KEY = "cookie_consent";

type ConsentValue = "all" | "essential" | null;

function getConsent(): ConsentValue {
  try {
    const v = localStorage.getItem(COOKIE_KEY);
    if (v === "all" || v === "essential") return v;
  } catch {}
  return null;
}

export function CookieConsent() {
  const { t } = useI18n();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Small delay so it doesn't flash on page load
    const timer = setTimeout(() => {
      if (!getConsent()) setVisible(true);
    }, 800);
    return () => clearTimeout(timer);
  }, []);

  const accept = (value: "all" | "essential") => {
    try { localStorage.setItem(COOKIE_KEY, value); } catch {}
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 inset-x-0 z-[100] p-3 sm:p-4 animate-fade-in">
      <div className="max-w-lg mx-auto bg-card border border-border rounded-2xl shadow-lg p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <Cookie className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm text-foreground leading-relaxed">
              {t("cookie.message")}{" "}
              <Link to="/cookies" className="underline text-primary hover:text-primary/80 transition-colors">
                {t("cookie.settings")}
              </Link>
            </p>
            <div className="flex flex-wrap gap-2 mt-3">
              <button
                onClick={() => accept("all")}
                className="px-4 py-2 text-sm font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                {t("cookie.accept")}
              </button>
              <button
                onClick={() => accept("essential")}
                className="px-4 py-2 text-sm font-medium rounded-lg border border-border text-foreground hover:bg-muted transition-colors"
              >
                {t("cookie.reject")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
